import { useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import {
  useGetDocsListQuery,
  useReenviarSFTPMutation,
} from "../../../services/docs";
import { Spinner } from "../../../components/Spinner";
import { ItemDocumentoComponente } from "../../../components/ItemDocumentoComponent";

export const ExpendienteScreen = () => {
  const { siniestro = "0", tramite_id = "0" } = useParams();

  // Queries & Mutations
  const { data, isLoading } = useGetDocsListQuery({
    siniestro,
    tramite: tramite_id,
  });

  const [reenviarAction, { isLoading: isReenviando }] = useReenviarSFTPMutation();

  // Función de Acción: Reenviar SFTP
  const reenviar = async () => {
    try {
      const resp = await reenviarAction({
        siniestro,
        tramite: tramite_id,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Expediente reenviado correctamente");
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error(err?.data?.message || "Ocurrió un error al reenviar el expediente");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* --- Barra de Acción Global y Leyenda --- */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col items-center justify-between gap-4 border-b border-slate-100 p-4 sm:flex-row">
          <div className="flex items-center gap-3 text-sm font-bold text-slate-800">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            </div>
            Visualización de Expediente
          </div>

          <div className="flex w-full justify-end sm:w-auto">
            <button
              onClick={reenviar}
              disabled={isReenviando}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-50"
            >
              {isReenviando ? <Spinner /> : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                  Reenviar Expediente (SFTP)
                </>
              )}
            </button>
          </div>
        </div>

        {/* Leyenda de Códigos de Estatus */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 px-5 py-3 text-xs">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Código de Estatus:
          </span>
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-400 ring-2 ring-slate-100"></span>
              <span className="font-medium text-slate-600">No Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100"></span>
              <span className="font-medium text-slate-700">Documento Cargado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-brand-500 ring-2 ring-brand-100"></span>
              <span className="font-medium text-slate-700">Digital Verificado</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-100"></span>
              <span className="font-medium text-slate-700">Digital Rechazado</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- Grid: Documentos Titular --- */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-1.5 rounded bg-brand-600"></div>
          <h3 className="text-sm font-bold tracking-tight text-slate-800">
            Documentos del Titular
          </h3>
          <span className="text-xs font-normal text-slate-400">
            ({data?.documentos?.length || 0} registrados)
          </span>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {data?.documentos?.length > 0 ? (
            data.documentos.map((doc: any, index: number) => (
              <ItemDocumentoComponente
                key={`${doc.id}-${index}`}
                doc={doc}
                index={index}
              />
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 py-8 text-slate-500">
              <p className="text-sm font-medium">No hay documentos registrados del titular.</p>
            </div>
          )}
        </div>
      </section>

      {/* --- Grid: Documentos Adicionales --- */}
      {data?.adicionales && data.adicionales.length > 0 && (
        <section className="space-y-4 pt-4">
          <div className="flex items-center gap-2 border-t border-slate-200 pt-6">
            <div className="h-4 w-1.5 rounded bg-blue-400"></div>
            <h3 className="text-sm font-bold tracking-tight text-slate-800">
              Documentación Adicional
            </h3>
            <span className="text-xs font-normal text-slate-400">
              ({data.adicionales.length} registrados)
            </span>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {data.adicionales.map((doc: any, index: number) => (
              <ItemDocumentoComponente
                key={`${doc.id}-adic-${index}`}
                doc={doc}
                index={index}
              />
            ))}
          </div>
        </section>
      )}

    </div>
  );
};
